import { useReduxState } from 'hooks';
import { AdminPage } from 'components/admin';
import { PageInnerContent, PageSidebar } from 'components/page';

import type { UserPermission } from 'shared-types';
import type { NavLinkOrSubheading } from 'types';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  activeLink?: string;
  children: React.ReactNode;
}

const SiteSettingsPage = ({ activeLink = '', children }: Props) => {
  const { messages, serverConfig, user } = useReduxState();

  const isVuoadmin = user?.roles.includes('vuoadmin');
  const isSuperadmin = user?.roles.includes('superadmin');

  const hasPermission = (permission: UserPermission) => {
    return (
      user?.roles?.includes('admin') && user?.permissions.includes(permission)
    );
  };

  // admin or superadmin
  const adminLinks: (NavLinkOrSubheading & { isVisible: boolean })[] = [
    {
      href: '/admin/settings/home-page',
      icon: 'home' as const,
      title: messages.homePage,
      isVisible: isSuperadmin
    },
    {
      href: '/admin/settings/email',
      icon: 'envelope' as const,
      title: messages.commonEmail,
      isVisible: isSuperadmin || hasPermission('manage-email-templates')
    },
    {
      href: '/admin/settings/custom-pages',
      icon: ['far', 'file-code'] as IconProp,
      title: messages.siteSettingsCustomPages,
      isVisible: serverConfig?.customPages?.length >= 1
    },
    {
      href: '/admin/settings/account',
      icon: 'user' as const,
      title: messages.siteSettingsAccountAndLogin,
      isVisible:
        isSuperadmin && serverConfig.registerEnableSettingsForSuperadmin
    },
    {
      href: '/admin/settings/two-factor-auth',
      icon: 'mobile-alt' as const,
      title: messages.authTwoFactorAuthentication,
      isVisible: isSuperadmin && serverConfig.authSharedTwoFactor.isEnabled
    },
    {
      href: '/admin/settings/calendar-events',
      icon: ['far', 'calendar-days'] as IconProp,
      title: messages.commonCalendarEvents,
      isVisible:
        isSuperadmin &&
        serverConfig.calendarEventFeedbackTemplatesSuperadminVisible &&
        serverConfig.featureFlags.enableFeedbackForCalendarEvents
    },
    {
      href: '/admin/settings/user-attributes',
      icon: 'tag' as const,
      title: messages.userAttributes,
      isVisible: isSuperadmin || hasPermission('edit-user-custom-attributes')
    },
    {
      href: '/admin/settings/ai-tools',
      icon: 'wand-magic-sparkles' as const,
      title: messages.siteSettingsAITools,
      isVisible: isSuperadmin
    }
  ].filter((link) => link.isVisible);

  // vuoadmin
  const vuoadminLinks: NavLinkOrSubheading[] = [
    {
      title: messages.siteSettingsCommon,
      type: 'subheading'
    },
    {
      href: '/admin/settings',
      icon: 'cog',
      isSublink: true,
      title: messages.siteSettings
    },
    {
      href: '/admin/settings/localization',
      icon: 'language',
      isSublink: true,
      title: messages.siteSettingsLanguageAndTime
    },
    {
      href: '/admin/settings/email',
      icon: 'envelope',
      isSublink: true,
      title: messages.commonEmail
    },
    {
      title: 'Styling',
      type: 'subheading'
    },
    {
      href: '/admin/settings/themes',
      icon: 'palette',
      isSublink: true,
      title: messages.siteThemes
    },
    {
      href: '/admin/settings/reader-themes',
      icon: 'palette',
      isSublink: true,
      title: messages.siteReaderThemes
    },
    {
      href: '/admin/settings/certificates',
      icon: 'certificate',
      isSublink: true,
      title: messages.siteCertificatesTemplates
    },
    {
      title: messages.commonFiles,
      type: 'subheading'
    },
    {
      href: '/admin/settings/files',
      icon: 'file',
      isSublink: true,
      title: messages.commonFiles
    },
    {
      href: '/admin/settings/pdf-exports',
      icon: 'file-pdf',
      isSublink: true,
      title: 'PDF exports'
    },
    {
      title: 'Pages',
      type: 'subheading'
    },
    {
      href: '/admin/settings/home-page',
      icon: 'home',
      isSublink: true,
      title: messages.homePage
    },
    {
      href: '/admin/settings/reader',
      icon: 'book',
      isSublink: true,
      title: messages.siteReaderSettings
    },
    {
      href: '/admin/settings/page-content',
      icon: 'code',
      isSublink: true,
      title: 'Page content'
    },
    {
      href: '/admin/settings/custom-pages',
      icon: ['far', 'file-code'],
      isSublink: true,
      title: messages.siteSettingsCustomPages
    },
    {
      title: 'Users and accounts',
      type: 'subheading'
    },
    {
      href: '/admin/settings/account',
      icon: 'user',
      isSublink: true,
      title: messages.siteSettingsAccountAndLogin
    },
    {
      href: '/admin/settings/users',
      icon: 'user',
      isSublink: true,
      title: messages.usersUsers
    },
    {
      href: '/admin/settings/groups',
      icon: 'users',
      isSublink: true,
      title: messages.commonGroups
    },
    {
      href: '/admin/settings/supervisors',
      icon: 'sitemap',
      isSublink: true,
      title: messages.adminSupervisors
    },
    {
      href: '/admin/settings/user-attributes',
      icon: 'tag',
      isSublink: true,
      title: messages.userAttributes
    },
    {
      title: messages.commonContent,
      type: 'subheading'
    },
    {
      href: '/admin/settings/products',
      icon: 'tag',
      isSublink: true,
      title: 'Products'
    },
    {
      href: '/admin/settings/courses',
      icon: 'book',
      isSublink: true,
      title: messages.siteSettingsCoursesAndContent
    },
    {
      href: '/admin/settings/paths',
      icon: 'tasks',
      isSublink: true,
      title: messages.commonPaths
    },
    {
      href: '/admin/settings/achievements',
      icon: 'check-circle',
      isSublink: true,
      title: messages.commonAchievementsAlt
    },
    {
      href: '/admin/settings/calendar-events',
      icon: ['far', 'calendar-days'],
      isSublink: true,
      title: messages.commonCalendarEvents
    },
    {
      href: '/admin/settings/competencies',
      icon: 'medal',
      isSublink: true,
      title: messages.commonUserCompetencies
    },
    {
      title: messages.commonAnalytics,
      type: 'subheading'
    },
    {
      href: '/admin/settings/analytics',
      icon: 'chart-line',
      isSublink: true,
      title: messages.commonAnalytics
    },
    {
      title: messages.commonAuthentication,
      type: 'subheading'
    },
    {
      href: '/admin/settings/login-settings',
      icon: 'sign-in-alt',
      isSublink: true,
      title: 'Login settings'
    },
    {
      href: '/admin/settings/login-page',
      icon: 'code',
      isSublink: true,
      title: 'Login page'
    },
    {
      href: '/admin/settings/two-factor-auth',
      icon: 'mobile-alt',
      isSublink: true,
      title: messages.authTwoFactorAuthentication
    },
    {
      href: '/admin/settings/single-sign-on',
      icon: 'key',
      isSublink: true,
      title: messages.commonSingleSignOn
    },
    {
      title: messages.settingsIntegrations,
      type: 'subheading'
    },
    {
      href: '/admin/settings/integrations-old',
      icon: 'share-alt',
      isSublink: true,
      title: 'Integrations old'
    },
    {
      href: '/admin/settings/integrations',
      icon: 'share-alt',
      isSublink: true,
      title: messages.settingsIntegrations
    },
    {
      href: '/admin/settings/external-api',
      icon: 'exchange-alt',
      title: messages.settingsExternalApi,
      isSublink: true
    },
    {
      href: '/admin/settings/ai-tools',
      icon: 'wand-magic-sparkles',
      title: messages.siteSettingsAITools,
      isSublink: true
    },
    {
      href: '/admin/settings/background-jobs',
      icon: 'clipboard-list',
      title: messages.siteSettingsBackgroundJobs,
      isSublink: true
    },
    {
      title: messages.commonActivity,
      type: 'subheading'
    },
    {
      href: '/admin/settings/integrations-changelog',
      icon: 'history',
      title: 'Integrations changelog',
      isSublink: true
    }
  ];

  const sidebarLinks = isVuoadmin ? vuoadminLinks : adminLinks;

  return (
    <AdminPage
      activeLink={
        user?.roles.includes('vuoadmin')
          ? '/admin/settings'
          : user?.roles.includes('superadmin')
          ? '/admin/settings/home-page'
          : '/admin/settings/user-attributes'
      }
      withSidebar={true}
    >
      <PageSidebar
        active={`/admin/settings${activeLink}`}
        links={sidebarLinks}
      />
      <PageInnerContent>{children}</PageInnerContent>
    </AdminPage>
  );
};

export default SiteSettingsPage;
